import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Modal } from '../ui/Modal';
import { Folder } from '../../types/api';
import { useUpdateFolder } from '../../services/folders';
import { useCurrentUser } from '../../services/userHooks';
import { useToast } from '../../hooks/useToast';
import { buildSaveEmail } from '../../utils/inboundEmail';
import clsx from 'clsx';

interface FolderEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: Folder;
}

function FolderEditModal({ isOpen, onClose, folder }: FolderEditModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const { showToast } = useToast();
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(folder.displayTitle);
  const [syncToMobile, setSyncToMobile] = useState(folder.syncToMobile);
  const [isPublic, setIsPublic] = useState(folder.public);
  const [error, setError] = useState<string | null>(null);

  const updateFolderMutation = useUpdateFolder();
  const folderEmail = buildSaveEmail(currentUser, folder.id);

  // reset form when modal opens/closes or folder changes
  useEffect(() => {
    if (isOpen) {
      setTitle(folder.displayTitle);
      setSyncToMobile(folder.syncToMobile);
      setIsPublic(folder.public);
      setError(null);
    }
  }, [isOpen, folder]);

  async function handleCopyEmail() {
    if (!folderEmail) return;
    try {
      await navigator.clipboard.writeText(folderEmail);
      showToast(t`Copied to clipboard`, 'success');
    } catch {
      showToast(t`Couldn't copy to clipboard`, 'error');
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError(t`Folder title is required`);
      return;
    }

    if (title.trim().length > 250) {
      setError(t`Folder title must be 250 characters or less`);
      return;
    }

    if (!currentUser?.formKey) {
      setError(t`Authentication required`);
      return;
    }

    try {
      const updatedFolder = await updateFolderMutation.mutateAsync({
        folderId: folder.id,
        title: title.trim(),
        syncToMobile,
        isPublic,
        formKey: currentUser.formKey,
      });

      // If we're on the folder page, redirect to the new slug
      if (location.pathname.startsWith('/folder/') && updatedFolder?.slug) {
        navigate({
          to: '/folder/$slug',
          params: { slug: updatedFolder.slug },
        });
      }

      onClose();
    } catch (err) {
      setError((err as Error).message || t`Sorry, there was an error. Please try again.`);
    }
  };

  const isPending = updateFolderMutation.isPending;

  return (
    <Modal.Root
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={t`Edit folder`}
      initialFocusRef={titleInputRef}
      className="w-full max-w-md"
    >
      <Modal.Header title={t`Edit Folder`} />
      <Modal.Body>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="folder-title" className="form-label">
              <Trans>Title</Trans>
            </label>
            <input
              ref={titleInputRef}
              id="folder-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t`Folder Title (required)`}
              maxLength={250}
              className={clsx('form-input', error && !title.trim() && 'form-input-error')}
              disabled={isPending}
            />
          </div>

          {folderEmail && (
            <div className="mb-5">
              <label htmlFor="folder-save-email" className="form-label">
                <Trans>Save to folder email</Trans>
              </label>
              <div className="flex gap-2">
                <input
                  id="folder-save-email"
                  type="text"
                  readOnly
                  value={folderEmail}
                  onFocus={(e) => e.target.select()}
                  className="form-input flex-fluid font-mono text-sm"
                  aria-describedby="folder-save-email-helper"
                />
                <button
                  type="button"
                  className="button-secondary shrink-0"
                  onClick={handleCopyEmail}
                  aria-label={t`Copy ${folderEmail}`}
                >
                  <Trans>Copy</Trans>
                </button>
              </div>
              <div id="folder-save-email-helper" className="form-helper">
                <Trans>Emails and links sent here are saved to this folder</Trans>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="folder-sync-to-mobile"
              className="flex cursor-pointer items-start"
            >
              <input
                id="folder-sync-to-mobile"
                type="checkbox"
                checked={syncToMobile}
                onChange={(e) => setSyncToMobile(e.target.checked)}
                className="border-surface-hover text-copy-primary focus:ring-primary mt-1 mr-3 h-4 w-4 rounded focus:ring-2"
                disabled={isPending}
              />
              <div>
                <div className="form-label">
                  <Trans>Sync folder to mobile apps</Trans>
                </div>
                <div className="form-helper">
                  <Trans>Include this folder in your mobile devices</Trans>
                </div>
              </div>
            </label>
          </div>

          <div className="mb-6">
            <label htmlFor="folder-public" className="flex cursor-pointer items-start">
              <input
                id="folder-public"
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="border-surface-hover text-copy-primary focus:ring-primary mt-1 mr-3 h-4 w-4 rounded focus:ring-2"
                disabled={isPending}
              />
              <div>
                <div className="form-label">
                  <Trans>Visible to anyone with link</Trans>
                </div>
                <div className="form-helper">
                  <Trans>Make this folder publicly accessible</Trans>
                </div>
              </div>
            </label>
          </div>

          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Actions>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isPending}
            className={clsx('button-primary')}
          >
            {updateFolderMutation.isPending ? (
              <Trans>Saving...</Trans>
            ) : (
              <Trans>Save Changes</Trans>
            )}
          </button>
        </Modal.Actions>
      </Modal.Footer>
    </Modal.Root>
  );
}

export default FolderEditModal;
